import { api } from './api';

/**
 * Staking domain — the contract between the TMA and the (not yet written)
 * `/v1/staking/*` backend. Shapes follow the product model from the INCPT
 * prototype: three open vaults plus a closed legacy line that existing
 * positions keep running on until their term ends.
 *
 * Amounts are plain USD numbers (USDT ≈ $1), matching the rest of the app.
 * Timestamps are ISO 8601 strings, as everywhere else in the API.
 */

/** The three vaults currently on offer. */
export type VaultKind = 'liquid' | 'monthly' | 'maximum';

/**
 * What a position lets the user take out:
 * - `full`    — principal and profit, at any time (Liquid)
 * - `profit`  — profit only; principal keeps working until the term ends (Monthly)
 * - `lock`    — nothing until the lock-up ends, then everything (Maximum)
 */
export type WithdrawMode = 'full' | 'profit' | 'lock';

/**
 * How often accrued profit is *paid out*. Every vault compounds daily
 * (`dailyRate`); this only says when the user can take the profit.
 */
export type AccrualPeriod = 'daily' | 'monthly' | 'term_end';

/** Whether a vault's cap applies to one position or to the sum of them all. */
export type LimitScope = 'position' | 'kind';

export interface Vault {
  kind: VaultKind;
  /** Advertised APY range, percent: Liquid 20–40, Monthly 50–70, Maximum 80–100. */
  apyMin: number;
  apyMax: number;
  /** Daily compounding rate as a fraction: Liquid 0.000719, Monthly 0.00129, Maximum 0.00176. */
  dailyRate: number;
  termDays: number;
  minAmount: number;
  /** Cap in USD: Liquid 20 000, Monthly and Maximum 50 000. */
  maxAmount: number;
  limitScope: LimitScope;
  /** Liquid allows a single open position; the others are unbounded (null). */
  maxOpenPositions: number | null;
  withdrawMode: WithdrawMode;
  accrual: AccrualPeriod;
  topUpAllowed: boolean;
  /**
   * Closed for new deposits. The vault still renders — its terms stay readable
   * — but the deposit form is replaced by a locked note, and the backend
   * rejects an `open` with `vault_closed`.
   */
  closed: boolean;
}

/**
 * A discontinued plan. No new positions are opened on these — they exist only
 * so positions already running on them can be rendered with their old fixed
 * rate until the term ends.
 */
export interface LegacyPlan {
  id: string;
  /** Fixed monthly rate as a fraction: 0.03 / 0.05 / 0.07. */
  monthlyRate: number;
  /** Effective APY, percent — compounded, so 3%/mo reads as 42.6. */
  apy: number;
  withdrawMode: WithdrawMode;
  accrual: AccrualPeriod;
}

export interface StakePosition {
  id: number;
  /** Set for a current-line position; null for a legacy one. */
  vaultKind: VaultKind | null;
  /** Set for a legacy position; null for a current-line one. Exactly one of the two. */
  legacyPlanId: string | null;
  invested: number;
  /** Profit accrued to date, including what has already been taken out. */
  profit: number;
  withdrawn: number;
  /** invested + profit − withdrawn. Computed server-side so the two agree. */
  current: number;
  /** What can be taken out right now under the position's withdraw mode; 0 while locked. */
  available: number;
  openedAt: string;
  /** When the soonest deposit tranche next earns; null if nothing is accruing. */
  nextAccrualAt: string | null;
  /** Only for `lock` positions; null otherwise. */
  lockEndsAt: string | null;
  /** When a `profit` vault next releases profit; null when that isn't its mode
   *  or the term has already ended and everything is free. */
  nextWithdrawAt?: string | null;
  status: 'active' | 'closed';
}

export interface StakingSummary {
  /** Everything currently staked, across both lines — the hero figure. */
  total: number;
  invested: number;
  profit: number;
  /** Staked per vault, for the three stat tiles. */
  byVault: Record<VaultKind, number>;
  /** Staked on the legacy line. */
  legacy: number;
}

/** The documents linked under the stake screen, editable from the admin panel. */
export interface StakingLegal {
  terms: string;
  aml: string;
  privacy: string;
}

export interface StakeTapState {
  likes: number;
  likeBonus: number;
  goal: number;
  rewardUsd: number;
  /** Last flush seq the server accepted — client continues from lastSeq + 1. */
  lastSeq?: number;
}

export interface StakingOverview {
  /** False when the vault is off and the user is not on the TG allowlist. */
  visible: boolean;
  vaults: Vault[];
  legacyPlans: LegacyPlan[];
  positions: StakePosition[];
  summary: StakingSummary;
  legal: StakingLegal;
  /** USDT TRC-20 address a deposit is paid to; null = staking unavailable. */
  treasuryTrx: string | null;
  /** Flat USDT fees on wallet / external withdrawals. */
  fees?: { walletUsd: number; externalUsd: number };
  /** Hours between accrual credits (admin-configurable; default 24). */
  accrualEveryHours?: number;
  tap: StakeTapState;
}

/** Where a withdrawal lands. Card payout is disabled. */
export type WithdrawDestination =
  | { type: 'wallet' }
  | { type: 'external'; chain: 'trx'; address: string };

export interface WithdrawQuote {
  amount: number;
  fee: number;
  /** amount − fee: what actually arrives. */
  receive: number;
  /** Wallet and card payouts are instant; an external transfer is queued. */
  instant: boolean;
}

/* ── API ──────────────────────────────────────────────────────────────────── */

/** Everything the stake screen renders, in one round trip. */
export const fetchStaking = (initData: string) => api<StakingOverview>(initData, '/v1/staking');

export type StakeTapResult = {
  tap: StakeTapState & {
    claimed: number;
    accepted: number;
    /** How many $1 credits this flush produced (0 if none). */
    rewards: number;
  };
  position?: StakePosition;
};

/** Flush a buffered tap batch. Same `seq` is idempotent on retry. */
export const syncStakeTaps = (initData: string, body: { seq: number; count: number }) =>
  api<StakeTapResult>(initData, '/v1/staking/tap', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/**
 * Open a position for a USDT payment that has already settled to the staking
 * treasury. The backend credits whatever actually arrived, so the client never
 * decides the amount.
 */
export const openPosition = (
  initData: string,
  body: { vault_kind: VaultKind; payment_transfer_id: string },
  idempotencyKey: string,
) =>
  api<{ position: StakePosition }>(initData, '/v1/staking/positions', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(body),
  });

/** Fee preview — call before showing the confirm step. */
export const withdrawQuote = (
  initData: string,
  id: number,
  body: { amount: number; destination: WithdrawDestination },
) =>
  api<WithdrawQuote>(initData, `/v1/staking/positions/${id}/withdraw/quote`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const withdrawFromPosition = (
  initData: string,
  id: number,
  body:
    | { amount: number; destination: WithdrawDestination }
    | { withdraw_all: true; destination: WithdrawDestination },
  idempotencyKey: string,
  reauthToken?: string,
) =>
  api<{ withdrawalId: string; quote: WithdrawQuote; position: StakePosition }>(
    initData,
    `/v1/staking/positions/${id}/withdraw`,
    {
      method: 'POST',
      headers: {
        'Idempotency-Key': idempotencyKey,
        ...(reauthToken ? { 'X-Reauth': reauthToken } : {}),
      },
      body: JSON.stringify(body),
    },
  );

/* ── Errors ───────────────────────────────────────────────────────────────── */

/**
 * Error codes the backend is expected to return, mirroring the card flow: the
 * code travels inside the JSON body that `api()` embeds in the thrown message.
 */
export type StakingErrorCode =
  | 'amount_below_min'
  | 'amount_above_max'
  | 'limit_exceeded'
  | 'position_already_open'
  | 'topup_not_allowed'
  | 'lock_active'
  | 'insufficient_available'
  | 'insufficient_balance'
  | 'vault_closed'
  | 'payment_already_used'
  | 'staking_unavailable'
  | 'temporarily_unavailable'
  | 'destination_invalid'
  | 'amount_below_fee'
  | 'profit_window_closed'
  | 'liquid_required';

/** Map a thrown staking error to a friendly message (see `cardErrorMessage`). */
export function stakingErrorMessage(
  raw: string,
  t: (k: string, o?: Record<string, unknown>) => string,
  fallback: string,
): string {
  const has = (code: StakingErrorCode) => raw.includes(code);
  if (has('amount_below_min')) return t('stake.errMin');
  if (has('amount_above_max')) return t('stake.errAboveMax');
  if (has('limit_exceeded')) return t('stake.errLimit');
  if (has('position_already_open')) return t('stake.errAlreadyOpen');
  if (has('topup_not_allowed')) return t('stake.errNoTopUp');
  if (has('lock_active')) return t('stake.errLocked');
  if (has('profit_window_closed')) return t('stake.errProfitWindow');
  if (has('insufficient_available')) return t('stake.errAvailable');
  if (has('insufficient_balance')) return t('send.errInsufficient');
  if (has('vault_closed')) return t('stake.errClosed');
  if (has('payment_already_used')) return t('stake.errPaymentUsed');
  if (has('staking_unavailable') || has('temporarily_unavailable'))
    return t('stake.errUnavailable');
  if (has('destination_invalid')) return t('stake.errDestination');
  if (has('amount_below_fee')) return t('stake.errBelowFee');
  if (has('liquid_required')) return t('stake.tapNeedLiquid');
  return fallback;
}

/* ── Derived values ───────────────────────────────────────────────────────── */

const DAY_MS = 86_400_000;

/** Whether a position runs on the discontinued line. */
export const isLegacy = (p: StakePosition) => p.legacyPlanId !== null;

/** Profit still in the position — what the "profit" half of the bar measures. */
export const unrealizedProfit = (p: StakePosition) => Math.max(0, p.profit - p.withdrawn);

/** How many days the position has been running. Used by the share card. */
export const daysRunning = (p: StakePosition, now = Date.now()) =>
  Math.max(1, Math.round((now - new Date(p.openedAt).getTime()) / DAY_MS));

/** Return on the principal, in percent — booked profit only. */
export const roi = (p: StakePosition) => (p.invested > 0 ? (p.profit / p.invested) * 100 : 0);

/** Milliseconds until profit next lands; 0 once it is due or unknown. */
export const msToAccrual = (p: StakePosition, now = Date.now()) =>
  p.nextAccrualAt ? Math.max(0, new Date(p.nextAccrualAt).getTime() - now) : 0;

/**
 * How far through the current accrual interval we are, 0–1.
 * `periodHours` comes from admin (default 24); monthly UI still uses ~30d.
 */
export function accrualProgress(
  p: StakePosition,
  accrual: AccrualPeriod,
  now = Date.now(),
  periodHours = 24,
) {
  if (accrual === 'term_end') return lockProgress(p, now);
  const span = accrual === 'daily' ? Math.max(1, periodHours) * 3_600_000 : 30 * DAY_MS;
  return Math.max(0.05, Math.min(1, (span - msToAccrual(p, now)) / span));
}

/** How far through the lock-up, 0–1. Returns 1 for a position that isn't locked. */
export function lockProgress(p: StakePosition, now = Date.now()) {
  if (!p.lockEndsAt) return 1;
  const start = new Date(p.openedAt).getTime();
  const end = new Date(p.lockEndsAt).getTime();
  if (end <= start) return 1;
  return Math.max(0, Math.min(1, (now - start) / (end - start)));
}

/** Whole days left on the lock-up; 0 once it has elapsed or never applied. */
export const lockDaysLeft = (p: StakePosition, now = Date.now()) =>
  p.lockEndsAt ? Math.max(0, Math.ceil((new Date(p.lockEndsAt).getTime() - now) / DAY_MS)) : 0;

/** True while the lock-up still blocks any withdrawal. */
export const isLocked = (p: StakePosition, now = Date.now()) =>
  !!p.lockEndsAt && new Date(p.lockEndsAt).getTime() > now;

/**
 * What one accrual period pays at the current balance — the headline figure on
 * the share card ("earned today" for a daily vault, "this month" otherwise).
 */
export const periodEarning = (p: StakePosition, dailyRate: number, accrual: AccrualPeriod) =>
  p.current * dailyRate * (accrual === 'daily' ? 1 : 30);
